import type {
    DistributionNodeData,
    FormulaNodeData,
    SimulationGraph
} from '../../models/simulation';
import {
    buildNameToIdMap,
    extractVariableNames,
    replaceVariableReferences,
    validateExpression
} from '../../utils/expressionParser';
import { topologicalSort } from '../../utils/graph';
import { validateSafeFormulaExpression } from '../../utils/units';

export interface ValidationError {
    nodeId: string;
    nodeName: string;
    message: string;
}

const isFiniteNumber = (value: unknown): value is number => Number.isFinite(value as number);

const validateDistributionNode = (nodeId: string, node: DistributionNodeData): ValidationError[] => {
    const issues: string[] = [];
    const { config, name } = node;

    switch (config.kind) {
        case 'fixed': {
            if (!isFiniteNumber(config.value)) {
                issues.push(`Must have a numeric value.`);
            }
            break;
        }
        case 'normal': {
            if (!isFiniteNumber(config.mean)) {
                issues.push(`Must have a numeric mean.`);
            }
            if (!isFiniteNumber(config.std)) {
                issues.push(`Must have a numeric standard deviation.`);
            } else if (config.std < 0) {
                issues.push(`Standard deviation cannot be negative.`);
            }
            if (config.min !== undefined && config.max !== undefined && config.min > config.max) {
                issues.push(`Minimum cannot be greater than maximum.`);
            }
            break;
        }
        case 'triangular': {
            if (!isFiniteNumber(config.min) || !isFiniteNumber(config.mode) || !isFiniteNumber(config.max)) {
                issues.push(`Must have numeric minimum, mode, and maximum.`);
                break;
            }
            if (config.min > config.max) {
                issues.push(`Minimum cannot be greater than maximum.`);
            }
            if (config.mode < config.min || config.mode > config.max) {
                issues.push(`Mode must be between minimum and maximum.`);
            }
            break;
        }
        case 'uniform': {
            if (!isFiniteNumber(config.min) || !isFiniteNumber(config.max)) {
                issues.push(`Must have numeric minimum and maximum.`);
                break;
            }
            if (config.min >= config.max) {
                issues.push(`Minimum must be less than maximum.`);
            }
            break;
        }
        default: {
            issues.push(`Has an unsupported configuration.`);
        }
    }

    return issues.map((message) => ({ nodeId, nodeName: name, message }));
};

const validateFormulaNode = (nodeId: string, node: FormulaNodeData, nameToIdMap: Map<string, string>): ValidationError[] => {
    const issues: string[] = [];
    const expression = node.expression ?? '';

    if (expression.trim().length === 0) {
        return [{ nodeId, nodeName: node.name, message: 'Expression cannot be empty.' }];
    }

    const validation = validateExpression(expression);
    if (!validation.valid && validation.error) {
        return [{ nodeId, nodeName: node.name, message: validation.error }];
    }

    const variableNames = extractVariableNames(expression);
    variableNames
        .filter((variable) => !nameToIdMap.has(variable))
        .forEach((variable) => {
            issues.push(`References missing node "${variable}".`);
        });

    const { expression: sanitizedExpression } = replaceVariableReferences(expression, variableNames);
    const formulaValidation = validateSafeFormulaExpression(sanitizedExpression);

    if (!formulaValidation.valid && formulaValidation.error) {
        issues.push(formulaValidation.error);
    }

    return issues.map((message) => ({ nodeId, nodeName: node.name, message }));
};


export const collectSimulationRunErrors = (graph: SimulationGraph): ValidationError[] => {
    const issues: ValidationError[] = [];

    if (!graph.nodes.length) {
        issues.push({ nodeId: '', nodeName: '', message: 'Add at least one node before running a simulation.' });
        return issues;
    }

    const nameToIdMap = buildNameToIdMap(graph.nodes);

    graph.nodes.forEach((node) => {
        switch (node.data.type) {
            case 'distribution':
                issues.push(...validateDistributionNode(node.id, node.data));
                break;
            case 'formula':
                issues.push(...validateFormulaNode(node.id, node.data, nameToIdMap));
                break;
            default:
                break;
        }
    });

    try {
        topologicalSort(graph.nodes, graph.edges);
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        issues.push({ nodeId: '', nodeName: '', message });
    }

    const uniqueMap = new Map<string, ValidationError>();
    issues.forEach((issue) => {
        const key = `${issue.nodeId}|${issue.message}`;
        if (!uniqueMap.has(key)) {
            uniqueMap.set(key, issue);
        }
    });
    return Array.from(uniqueMap.values());
};
